/**
 * portal-berechtigungen/hooks.ts — TanStack Query read + mutation hooks for Bereich 6.
 *
 * The api layer (api.ts) does the live/mock split, so these hooks are uniform. Because
 * EVERY backend mutation returns the full `{ mode, groups }` snapshot, the mutation hooks
 * ADOPT that snapshot straight into the cache (setQueryData) instead of round-tripping a
 * refetch — the web state is always exactly what the backend returned.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { qk } from '../../lib/query-keys';
import {
  deletePortalGruppe,
  fetchIgb2bKategorien,
  fetchPortalGruppen,
  fetchPortalMemberPool,
  fetchPortalVisibilityMode,
  savePortalGruppe,
  setPortalVisibilityMode,
  type PortalGruppeCurrent,
  type PortalGruppeDraft,
} from './api';
import type {
  Igb2bKategorieDto,
  PortalGroupsResponseDto,
  PortalMemberOption,
  PortalVisibilityMode,
  PortalVisibilityModeDto,
} from './types';

/** Adopt a returned { mode, groups } snapshot into BOTH the groups + mode caches. */
function adoptSnapshot(
  qc: QueryClient,
  subjectType: string,
  subjectId: string,
  snapshot: PortalGroupsResponseDto,
): void {
  qc.setQueryData(qk.portalGruppen(subjectType, subjectId), snapshot);
  qc.setQueryData<PortalVisibilityModeDto>(qk.portalVisibilityMode(subjectType, subjectId), {
    mode: snapshot.mode,
    groupCount: snapshot.groups.filter((g) => !g.istSystem).length,
  });
}

// ── Reads ─────────────────────────────────────────────────────────────────────

/** The fixed IGB2B taxonomy — reference data, cached hard. */
export function useIgb2bKategorien(enabled = true): UseQueryResult<Igb2bKategorieDto[]> {
  return useQuery<Igb2bKategorieDto[]>({
    queryKey: qk.igb2bKategorien(),
    queryFn: fetchIgb2bKategorien,
    staleTime: Infinity,
    enabled,
  });
}

/** The customer's portal-visibility mode + non-system group count. */
export function usePortalVisibilityMode(
  subjectType: string,
  subjectId: string,
  enabled = true,
): UseQueryResult<PortalVisibilityModeDto> {
  return useQuery<PortalVisibilityModeDto>({
    queryKey: qk.portalVisibilityMode(subjectType, subjectId),
    queryFn: () => fetchPortalVisibilityMode(subjectType, subjectId),
    enabled: enabled && Boolean(subjectType) && Boolean(subjectId),
  });
}

/** The customer's Portal-Berechtigungsgruppen ({ mode, groups } snapshot). */
export function usePortalGruppen(
  subjectType: string,
  subjectId: string,
  enabled = true,
): UseQueryResult<PortalGroupsResponseDto> {
  return useQuery<PortalGroupsResponseDto>({
    queryKey: qk.portalGruppen(subjectType, subjectId),
    queryFn: () => fetchPortalGruppen(subjectType, subjectId),
    enabled: enabled && Boolean(subjectType) && Boolean(subjectId),
  });
}

/** The assignable portal-user pool (primary holder + the customer's non-revoked grants). */
export function usePortalMemberPool(
  subjectType: string,
  subjectId: string,
  enabled = true,
): UseQueryResult<PortalMemberOption[]> {
  return useQuery<PortalMemberOption[]>({
    queryKey: qk.portalMemberPool(subjectType, subjectId),
    queryFn: () => fetchPortalMemberPool(subjectType, subjectId),
    enabled: enabled && Boolean(subjectType) && Boolean(subjectId),
  });
}

// ── Mutations (adopt the returned snapshot) ─────────────────────────────────────

/** PUT the mode; adopts the returned snapshot (group-mode seeds the default groups). */
export function useSetPortalVisibilityMode(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: PortalVisibilityMode) => setPortalVisibilityMode(subjectType, subjectId, mode),
    onSuccess: (snapshot) => adoptSnapshot(qc, subjectType, subjectId, snapshot),
  });
}

/** Create or edit a group via granular calls; adopts the final snapshot. */
export function useSavePortalGruppe(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string | null; draft: PortalGruppeDraft; current?: PortalGruppeCurrent }) =>
      savePortalGruppe(subjectType, subjectId, input),
    onSuccess: (snapshot) => adoptSnapshot(qc, subjectType, subjectId, snapshot),
  });
}

/** Delete a group; adopts the returned snapshot. */
export function useDeletePortalGruppe(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => deletePortalGruppe(subjectType, subjectId, groupId),
    onSuccess: (snapshot) => adoptSnapshot(qc, subjectType, subjectId, snapshot),
  });
}
